import {
  appendManualMessages,
  clearConversation,
  getWorkflowAgent,
} from "@mirage/service-conversation";
import type {
  UserMessageOptions,
  WorkflowAgent,
  WorkflowStepValuesType,
} from "@mirage/service-conversation/types";
import {
  convertChatSourceToDocumentInfo,
  convertUUIDsToDocumentInfo,
} from "@mirage/service-conversation/utils/convertChatSourceToDocumentInfo";
import { createConversationClientContext } from "@mirage/service-conversation/utils/createConversationClientContext";
import {
  DashSearch,
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";

export const initializeWorkflowAgentConversation = async (
  agentId: string,
  conversationId: string,
  source: string,
  setUserMessageOptions: (options: UserMessageOptions) => void,
): Promise<void> => {
  const agent = await getWorkflowAgent(agentId);

  const { workflow: workflowValues } = agent;
  const behavior = workflowValues["2"];
  const workflowPrompt = behavior.prompt as string;

  const workflow = workflowValues["3"] as {
    [key: string]: WorkflowStepValuesType;
  };
  // map workflow steps into an array
  const workflowSteps = Object.values(workflow).map(
    (step: WorkflowStepValuesType) => step,
  );
  const userPrompt = workflowSteps[0].prompt as string;

  // TODO: Add sources to each user message
  // const sources = workflowValues["4"].sources as string[];

  const message = `SYSTEM_PROMPT: ${workflowPrompt}`;

  // Add messages to the conversation using the service method directly
  await clearConversation(conversationId);
  await appendManualMessages([
    {
      conversationId,
      message,
      messageType: "userMessage",
      attachments:
        agent.accessType === "private"
          ? convertUUIDsToDocumentInfo(agent.sources)
          : (agent.sourceContents || []).map(convertChatSourceToDocumentInfo),
      source,
      clientContext: createConversationClientContext("workflow_agent", {
        clientScenarioId: JSON.stringify({
          agentId,
          model: agent.model,
        }),
      }),
    },
    {
      conversationId,
      message: userPrompt,
      messageType: "assistantMessage",
      source,
    },
  ]);
  setUserMessageOptions(getUserMessageOptionsFromWorkflowAgent(agent));
};

export const getUserMessageOptionsFromWorkflowAgent = (
  agent: WorkflowAgent,
): UserMessageOptions => {
  const { workflow: workflowValues } = agent;
  const behavior = workflowValues["2"];

  return {
    searchParams: (behavior["web-search"] as boolean)
      ? new SearchParams({
          searchTarget: {
            value: new DashSearch(),
            case: "dashSearch",
          },
        })
      : new SearchParams({
          searchTarget: {
            value: new NoSearch(),
            case: "noSearch",
          },
        }),
    agentIds: [agent.type],
    hideFollowUpSuggestions: !(behavior["suggestions"] as boolean),
    // we want to use the same agent for all messages in the conversation
    persistAgentIds: true,
    skipPersistence: agent.id === "preview",
  };
};
